import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Upload,
  Popconfirm,
  Tag,
  Tooltip,
  Row,
  Col,
  Typography,
  Divider
} from 'antd';
import {
  PlusOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  DownloadOutlined,
  KeyOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CloudUploadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { RcFile } from 'antd/es/upload';
import * as XLSX from 'xlsx';
import { UserWithRole, CreateUserRequest, UpdateUserRequest } from '../../../../shared/types';

const { Title, Text } = Typography;
const { Option } = Select;

interface Role {
  RoleId: number;
  RoleKey: string;
  RoleName: string;
}

interface ImportUserData {
  Username: string;
  Password: string;
  FullName: string;
  RoleName: string;
  Email?: string;
  Phone?: string;
}

interface ImportResult {
  inserted: UserWithRole[];
  errors: Array<{
    user: ImportUserData;
    error: string;
  }>;
}

interface UsersResponse {
  success: boolean;
  data: UserWithRole[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
  message: string;
}

const UserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Modal states
  const [modalVisible, setModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithRole | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);

  // Form instances
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  // Search and filters
  const [searchText, setSearchText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  // API Base URL
  const API_BASE = '/api/v1';

  // Load users với phân trang
  const loadUsers = async (page = 1, pageSize = 10, search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        search: search
      });

      const response = await fetch(`${API_BASE}/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: UsersResponse = await response.json();

      if (result.success) {
        setUsers(result.data);
        setPagination({
          current: result.pagination.currentPage,
          pageSize: result.pagination.pageSize,
          total: result.pagination.totalItems
        });
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      message.error('Lỗi khi tải danh sách người dùng');
    } finally {
      setLoading(false);
    }
  };

  // Load roles for dropdown
  const loadRoles = async () => {
    try {
      const response = await fetch(`${API_BASE}/users/roles`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        setRoles(result.data);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
      message.error('Lỗi khi tải danh sách vai trò');
    }
  };

  // Handle search
  const handleSearch = () => {
    loadUsers(1, pagination.pageSize, searchText);
  };

  // Handle table pagination change
  const handleTableChange = (page: number, pageSize?: number) => {
    loadUsers(page, pageSize || pagination.pageSize, searchText);
  };

  // Open create/edit modal
  const openModal = (user?: UserWithRole) => {
    setEditingUser(user || null);
    setModalVisible(true);

    if (user) {
      form.setFieldsValue({
        Username: user.Username,
        FullName: user.FullName,
        Email: user.Email,
        Phone: user.Phone,
        RoleId: user.RoleId
      });
    } else {
      form.resetFields();
    }
  };

  // Handle create/update user
  const handleSaveUser = async () => {
    try {
      const values = await form.validateFields();

      if (editingUser) {
        // Update user
        const updateData: UpdateUserRequest = {
          UserId: editingUser.UserId,
          ...values
        };

        const response = await fetch(`${API_BASE}/users/${editingUser.UserId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        });

        const result = await response.json();

        if (result.success) {
          message.success('Cập nhật người dùng thành công');
        } else {
          throw new Error(result.message);
        }
      } else {
        // Create new user
        const createData: CreateUserRequest = values;

        const response = await fetch(`${API_BASE}/users`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(createData)
        });

        const result = await response.json();

        if (result.success) {
          message.success('Tạo người dùng thành công');
        } else {
          throw new Error(result.message);
        }
      }

      setModalVisible(false);
      setEditingUser(null);
      form.resetFields();
      loadUsers(pagination.current, pagination.pageSize, searchText);

    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('Lỗi khi lưu người dùng');
      }
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userId: number) => {
    try {
      const response = await fetch(`${API_BASE}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        message.success('Xóa người dùng thành công');
        loadUsers(pagination.current, pagination.pageSize, searchText);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('Lỗi khi xóa người dùng');
      }
    }
  };

  // Open change password modal
  const openPasswordModal = (user: UserWithRole) => {
    setSelectedUser(user);
    setPasswordModalVisible(true);
    passwordForm.resetFields();
  };

  // Handle change password
  const handleChangePassword = async () => {
    if (!selectedUser) return;

    try {
      const values = await passwordForm.validateFields();

      const response = await fetch(`${API_BASE}/users/${selectedUser.UserId}/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newPassword: values.newPassword })
      });

      const result = await response.json();

      if (result.success) {
        message.success('Đổi mật khẩu thành công');
        setPasswordModalVisible(false);
        passwordForm.resetFields();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('Lỗi khi đổi mật khẩu');
      }
    }
  };

  // Handle create Moodle user
  const handleCreateMoodleUser = async (user: UserWithRole) => {
    Modal.confirm({
      title: 'Tạo người dùng trên Moodle',
      content: `Bạn có chắc muốn tạo tài khoản Moodle cho người dùng "${user.FullName}"?`,
      icon: <CloudUploadOutlined />,
      okText: 'Tạo',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          const response = await fetch(`${API_BASE}/moodle/create-user`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userId: user.UserId,
              fullName: user.FullName,
            }),
          });

          const result = await response.json();

          if (response.ok && result.success) {
            message.success(result.message || 'Tạo người dùng Moodle thành công');
          } else {
            message.error(result.error || `Lỗi không xác định: ${response.statusText}`);
          }
        } catch (error) {
          console.error('Lỗi khi tạo người dùng Moodle:', error);
          message.error('Không thể kết nối đến server hoặc có lỗi xảy ra.');
        }
      },
    });
  };

  // Handle Excel import
  const handleExcelImport = (file: RcFile) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false }) as any[];

        if (rows.length === 0) {
          message.error('File Excel không có dữ liệu');
          return;
        }

        // Parse Excel data
        const users: ImportUserData[] = rows.map((row, index) => {
          const username = row.Username || row.username || row['Tên đăng nhập'] || '';
          const password = row.Password || row.password || row['Mật khẩu'] || '';
          const fullName = row.FullName || row.fullName || row['Họ tên'] || row['Họ và tên'] || '';
          const roleName = row.RoleName || row.roleName || row['Vai trò'] || row.Role || '';
          const email = row.Email || row.email || null;
          const phone = row.Phone || row.phone || row['Số điện thoại'] || null;

          if (!username || !password || !fullName || !roleName) {
            throw new Error(`Dòng ${index + 2}: Thiếu thông tin bắt buộc (Username, Password, FullName, RoleName)`);
          }

          return {
            Username: username.toString().trim(),
            Password: password.toString().trim(),
            FullName: fullName.toString().trim(),
            RoleName: roleName.toString().trim(),
            Email: email ? email.toString().trim() : undefined,
            Phone: phone ? phone.toString().trim() : undefined
          };
        });

        // Confirm import
        Modal.confirm({
          title: 'Xác nhận import',
          content: `Bạn có chắc muốn import ${users.length} người dùng?`,
          icon: <ExclamationCircleOutlined />,
          onOk: () => importUsers(users),
          okText: 'Import',
          cancelText: 'Hủy'
        });

      } catch (error) {
        console.error('Error parsing Excel:', error);
        message.error(error instanceof Error ? error.message : 'Lỗi khi đọc file Excel');
      }
    };

    reader.readAsArrayBuffer(file);
    return false; // Prevent upload
  };

  // Import users to server
  const importUsers = async (usersData: ImportUserData[]) => {
    try {
      const response = await fetch(`${API_BASE}/users/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ users: usersData })
      });

      const result = await response.json();

      if (result.success) {
        const importResult: ImportResult = result.data;

        if (importResult.inserted.length > 0) {
          message.success(`Import thành công ${importResult.inserted.length} người dùng`);
        }

        if (importResult.errors.length > 0) {
          message.warning(`Có ${importResult.errors.length} lỗi khi import. Kiểm tra console để xem chi tiết.`);
          console.warn('Import errors:', importResult.errors);
        }

        loadUsers(pagination.current, pagination.pageSize, searchText);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Error importing users:', error);
      message.error('Lỗi khi import người dùng');
    }
  };

  // Download Excel template
  const downloadTemplate = () => {
    const templateData = [
      {
        'Username': 'admin',
        'Password': '123456',
        'FullName': 'Quản trị viên',
        'RoleName': 'Quản trị viên',
        'Email': 'admin@school.edu.vn',
        'Phone': '0123456789'
      },
      {
        'Username': 'giaovien1',
        'Password': '123456',
        'FullName': 'Nguyễn Văn A',
        'RoleName': 'Giáo viên',
        'Email': 'giaovien1@school.edu.vn',
        'Phone': '0987654321'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users Template');
    XLSX.writeFile(wb, 'users_template.xlsx');
  };

  // Table columns
  const columns: ColumnsType<UserWithRole> = [
    {
      title: 'ID',
      dataIndex: 'UserId',
      key: 'UserId',
      width: 80,
      sorter: true
    },
    {
      title: 'Tên đăng nhập',
      dataIndex: 'Username',
      key: 'Username',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Họ và tên',
      dataIndex: 'FullName',
      key: 'FullName'
    },
    {
      title: 'Email',
      dataIndex: 'Email',
      key: 'Email',
      render: (text) => text || <Text type="secondary">Chưa có</Text>
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'Phone',
      key: 'Phone',
      render: (text) => text || <Text type="secondary">Chưa có</Text>
    },
    {
      title: 'Vai trò',
      dataIndex: 'RoleName',
      key: 'RoleName',
      render: (text, record) => (
        <Tag color={record.RoleKey === 'admin' ? 'red' : record.RoleKey === 'teacher' ? 'blue' : 'green'}>
          {text}
        </Tag>
      )
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      render: (text) => new Date(text).toLocaleDateString('vi-VN')
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title="Sửa thông tin">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openModal(record)}
            />
          </Tooltip>
          <Tooltip title="Tạo tài khoản Moodle">
            <Button
              size="small"
              icon={<CloudUploadOutlined />}
              onClick={() => handleCreateMoodleUser(record)}
            />
          </Tooltip>

          <Tooltip title="Đổi mật khẩu">
            <Button
              type="default"
              size="small"
              icon={<KeyOutlined />}
              onClick={() => openPasswordModal(record)}
            />
          </Tooltip>

          <Popconfirm
            title="Xóa người dùng"
            description={`Bạn có chắc muốn xóa người dùng "${record.FullName}"?`}
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
            onConfirm={() => handleDeleteUser(record.UserId)}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Xóa">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              👥 Quản lý người dùng
            </Title>
            <Text type="secondary">
              Quản lý tài khoản và phân quyền người dùng trong hệ thống
            </Text>
          </Col>

          <Col>
            <Space>
              <Input.Search
                placeholder="Tìm kiếm theo tên, email..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={handleSearch}
                onPressEnter={handleSearch}
                style={{ width: 250 }}
                enterButton={<SearchOutlined />}
              />

              <Button
                icon={<ReloadOutlined />}
                onClick={() => loadUsers(pagination.current, pagination.pageSize, searchText)}
                title="Làm mới"
              />
            </Space>
          </Col>
        </Row>

        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openModal()}
              >
                Thêm người dùng
              </Button>

              <Upload
                accept=".xlsx,.xls"
                beforeUpload={handleExcelImport}
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />}>
                  Import Excel
                </Button>
              </Upload>

              <Button
                icon={<DownloadOutlined />}
                onClick={downloadTemplate}
              >
                Tải template
              </Button>
            </Space>
          </Col>

          <Col>
            <Text type="secondary">
              Tổng: {pagination.total} người dùng
            </Text>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="UserId"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} người dùng`,
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Modal tạo/sửa người dùng */}
      <Modal
        title={editingUser ? 'Sửa thông tin người dùng' : 'Thêm người dùng mới'}
        open={modalVisible}
        onOk={handleSaveUser}
        onCancel={() => {
          setModalVisible(false);
          setEditingUser(null);
          form.resetFields();
        }}
        width={600}
        okText="Lưu"
        cancelText="Hủy"
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="Username"
            label="Tên đăng nhập"
            rules={[
              { required: true, message: 'Vui lòng nhập tên đăng nhập' },
              { min: 3, message: 'Tên đăng nhập phải có ít nhất 3 ký tự' }
            ]}
          >
            <Input placeholder="Nhập tên đăng nhập" disabled={!!editingUser} />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="Password"
              label="Mật khẩu"
              rules={[
                { required: true, message: 'Vui lòng nhập mật khẩu' },
                { min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự' }
              ]}
            >
              <Input.Password placeholder="Nhập mật khẩu" />
            </Form.Item>
          )}

          <Form.Item
            name="FullName"
            label="Họ và tên"
            rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
          >
            <Input placeholder="Nhập họ và tên" />
          </Form.Item>

          <Form.Item
            name="RoleId"
            label="Vai trò"
            rules={[{ required: true, message: 'Vui lòng chọn vai trò' }]}
          >
            <Select placeholder="Chọn vai trò">
              {roles.map(role => (
                <Option key={role.RoleId} value={role.RoleId}>
                  {role.RoleName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="Email"
                label="Email"
                rules={[{ type: 'email', message: 'Email không hợp lệ' }]}
              >
                <Input placeholder="Nhập email" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="Phone"
                label="Số điện thoại"
              >
                <Input placeholder="Nhập số điện thoại" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Modal đổi mật khẩu */}
      <Modal
        title={`Đổi mật khẩu: ${selectedUser?.FullName}`}
        open={passwordModalVisible}
        onOk={handleChangePassword}
        onCancel={() => {
          setPasswordModalVisible(false);
          setSelectedUser(null);
          passwordForm.resetFields();
        }}
        okText="Đổi mật khẩu"
        cancelText="Hủy"
      >
        <Form
          form={passwordForm}
          layout="vertical"
        >
          <Form.Item
            name="newPassword"
            label="Mật khẩu mới"
            rules={[
              { required: true, message: 'Vui lòng nhập mật khẩu mới' },
              { min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự' }
            ]}
          >
            <Input.Password placeholder="Nhập mật khẩu mới" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Xác nhận mật khẩu"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Vui lòng xác nhận mật khẩu' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Mật khẩu xác nhận không khớp'));
                }
              })
            ]}
          >
            <Input.Password placeholder="Xác nhận mật khẩu mới" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagementPage; 